import React, { useMemo, useState } from 'react';
import { useProjectStore } from '../store/useProjectStore';
// @ts-ignore
import { Action, KanbanTaskStatus } from '../types';
import { 
  Clock, 
  CheckCircle2, 
  PlayCircle, 
  Calendar,
  User,
  AlertCircle,
  Flag,
  Hash,
  Plus,
  Ghost
} from 'lucide-react';

interface ActionsKanbanBoardProps {
  actions: Action[];
  onUpdateAction: (id: string, data: Partial<Action>) => void;
  onActionClick?: (actionId: string) => void;
  onEditAction?: (actionId: string) => void;
  onDeleteAction?: (actionId: string) => void;
  onAddAction?: (status: KanbanTaskStatus) => void; // Nova prop para adicionar
  zoomLevel?: number;
}

// Configuração Visual das Colunas
const COLUMNS_CONFIG = [
  { 
    id: 'pending' as const, 
    statusKey: 'todo' as KanbanTaskStatus,
    title: 'A FAZER', 
    icon: Clock, 
    headerBorder: 'border-t-slate-500', 
    badgeColor: 'border border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200',
    columnBg: 'bg-slate-200/50 dark:bg-[var(--bg-workspace)]' 
  },
  { 
    id: 'in_progress' as const, 
    statusKey: 'doing' as KanbanTaskStatus,
    title: 'EM ANDAMENTO', 
    icon: PlayCircle, 
    headerBorder: 'border-t-blue-700', 
    badgeColor: 'border border-slate-300 bg-slate-100 text-blue-700 dark:border-slate-700 dark:bg-slate-800 dark:text-blue-300',
    columnBg: 'bg-slate-200/50 dark:bg-[var(--bg-workspace)]'
  },
  { 
    id: 'concluded' as const, 
    statusKey: 'done' as KanbanTaskStatus,
    title: 'CONCLUÍDO', 
    icon: CheckCircle2, 
    headerBorder: 'border-t-emerald-700', 
    badgeColor: 'border border-slate-300 bg-slate-100 text-emerald-700 dark:border-slate-700 dark:bg-slate-800 dark:text-emerald-300',
    columnBg: 'bg-slate-200/50 dark:bg-[var(--bg-workspace)]'
  },
];

// Normalização de Status
const NORMALIZE_STATUS = (status: string | undefined) => {
    if (!status) return 'pending';
    const s = status.toLowerCase().trim();
    if (['todo', 'pending', 'a fazer', 'pendente'].some(v => s.includes(v))) return 'pending';
    if (['doing', 'in_progress', 'running', 'andamento', 'execucao'].some(v => s.includes(v))) return 'in_progress';
    if (['done', 'concluded', 'concluido', 'finalizado'].some(v => s.includes(v))) return 'concluded';
    return 'pending';
};

export const ActionsKanbanBoard: React.FC<ActionsKanbanBoardProps> = ({ 
  actions, 
  onUpdateAction, 
  onActionClick,
  onAddAction,
  zoomLevel = 1 
}) => {
  const store = useProjectStore(); 
  const cards = (store as any)?.cards || [];
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);

  const cardMap = useMemo(() => new Map(cards.map(c => [c.id, c])), [cards]);
  
  const getDisplayId = (actionId: string, linkedNodeId?: string) => {
    if (linkedNodeId && cardMap.has(linkedNodeId)) {
      return (cardMap.get(linkedNodeId) as any)?.numeroSequencial?.toString().padStart(3, '0');
    }
    return actionId.slice(-3);
  };

  const getGridClass = () => {
    const zoom = zoomLevel <= 2 ? zoomLevel * 100 : zoomLevel;
    if (zoom < 60) return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
    if (zoom < 85) return 'grid-cols-1 xl:grid-cols-2';
    return 'grid-cols-1';
  };

  const getDeadlineStatus = (deadline?: string, status?: string) => {
    if (!deadline || status === 'concluded' || status === 'done') return 'ok';
    const today = new Date(); today.setHours(0,0,0,0);
    const deadlineDate = new Date(deadline); deadlineDate.setHours(0,0,0,0);
    const diffTime = deadlineDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'expired';
    if (diffDays <= 3) return 'warning';
    return 'ok';
  };

  const PriorityFlag = ({ impacto }: { impacto?: number }) => {
    const level = impacto || 1;
    let colorClass = "text-slate-300 fill-slate-100"; 
    if (level === 3) colorClass = "text-red-500 fill-red-500"; 
    if (level === 2) colorClass = "text-amber-400 fill-amber-400"; 

    return <Flag size={12} className={colorClass} />;
  };

  // Handlers de Drag & Drop
  const handleDragStart = (e: React.DragEvent, id: string) => {
    e.dataTransfer.setData('actionId', id);
    setDraggedId(id);
  };
  
  const handleDragEnd = () => {
    setDraggedId(null);
    setDragOverColumn(null);
  };
  
  const handleDragOver = (e: React.DragEvent, colId: string) => {
    e.preventDefault();
    if (dragOverColumn !== colId) setDragOverColumn(colId);
  };
  
  const handleDrop = (e: React.DragEvent, targetStatusId: string) => {
    e.preventDefault();
    const actionId = e.dataTransfer.getData('actionId');
    
    // Encontrar o statusKey correto baseado no ID da coluna
    const targetColumn = COLUMNS_CONFIG.find(c => c.id === targetStatusId);
    if (targetColumn && actionId) {
      onUpdateAction(actionId, { status: targetColumn.statusKey });
    }
    
    setDraggedId(null);
    setDragOverColumn(null);
  };

  // Avatar Responsável
  const ResponsibleAvatar = ({ name }: { name?: string }) => {
    if (!name || name === '-' || name === 'N/A') return (
      <div className="flex items-center gap-1.5 text-slate-400 opacity-60">
        <div className="flex h-5 w-5 items-center justify-center border border-dashed border-slate-300 bg-slate-100">
          <User size={10} />
        </div>
        <span className="text-[10px]">Sem resp.</span>
      </div>
    );
    
    const initials = name.slice(0, 2).toUpperCase();
    const colors = ['bg-slate-200 text-blue-700', 'bg-slate-200 text-emerald-700', 'bg-slate-200 text-amber-700', 'bg-slate-200 text-violet-700'];
    const colorClass = colors[name.length % colors.length];
    
    return (
      <div className="flex items-center gap-1.5" title={name}>
        <div className={`flex h-5 w-5 items-center justify-center text-[9px] font-bold ${colorClass}`}>
          {initials}
        </div>
        <span className="text-[10px] font-medium text-slate-600 dark:text-slate-400 truncate max-w-[80px]">{name.split(' ')[0]}</span>
      </div>
    );
  };

  return (
    <div className="enterprise-kanban-shell h-full w-full overflow-hidden pb-3 font-sans">
      {COLUMNS_CONFIG.map((column) => {
        const safeActions = actions || [];
        const columnActions = safeActions.filter(
            a => NORMALIZE_STATUS(a.status) === column.id && a.status !== 'canceled'
        );
        const isDragOver = dragOverColumn === column.id;

        return (
          <div 
            key={column.id} 
            className={`
              enterprise-kanban-column flex h-full min-w-[248px] flex-1 flex-col overflow-hidden border border-enterprise-border-soft
              ${isDragOver ? 'border-enterprise-primary bg-enterprise-active/20' : 'enterprise-surface-2'}
            `}
            onDragOver={(e) => handleDragOver(e, column.id)}
            onDrop={(e) => handleDrop(e, column.id)}
          >
            {/* Header */}
            <div className={`enterprise-kanban-header flex shrink-0 items-center justify-between border-t-[3px] px-2.5 py-2 ${column.headerBorder}`}>
              <div className="flex items-center gap-2">
                <h3 className="enterprise-field-label text-xs font-semibold enterprise-text-primary">
                  {column.title}
                </h3>
                <span className={`px-1.5 py-0.5 text-[9px] font-semibold ${column.badgeColor}`}>
                  {columnActions.length}
                </span>
              </div>
              <button 
                onClick={() => onAddAction && onAddAction(column.statusKey)}
                className="p-1 enterprise-text-secondary hover:bg-[rgba(255,255,255,0.04)] hover:text-enterprise-primary"
                title={`Adicionar nova ação em ${column.title}`}
              >
                <Plus size={16} />
              </button>
            </div>

            {/* Container dos Cards */}
            <div className={`flex-1 overflow-y-auto p-1.5 custom-scrollbar ${column.columnBg}`}>
              <div className={`grid content-start gap-1.5 ${getGridClass()}`}>
                
                {columnActions.length === 0 ? (
                  <div className="enterprise-card-surface m-1 flex h-24 flex-col items-center justify-center border-dashed enterprise-text-muted enterprise-surface-2">
                    <Ghost size={24} className="mb-2 opacity-50" />
                    <span className="text-xs font-medium opacity-70">Sem ações</span>
                  </div>
                ) : (
                  columnActions.map(action => {
                    const statusDeadline = getDeadlineStatus(action.prazo, NORMALIZE_STATUS(action.status));
                    const isExpired = statusDeadline === 'expired';
                    const isCanceled = action.status === 'canceled';
                    
                    return (
                      <div 
                        key={action.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, action.id)}
                        onDragEnd={handleDragEnd}
                        onClick={() => onActionClick?.(action.id)}
                        className={`
                          enterprise-kanban-card group relative flex cursor-grab flex-col justify-between border border-enterprise-border-soft bg-enterprise-surface-2 p-2 active:cursor-grabbing
                          hover:bg-enterprise-surface-3
                          ${draggedId === action.id ? 'opacity-40 grayscale' : 'opacity-100'}
                          ${isCanceled ? 'enterprise-surface-2 opacity-60' : ''}
                          ${isExpired && !isCanceled ? 'border-l-4 border-l-[var(--risk-critical)]' : ''}
                        `}
                      >
                        {/* Topo: ID e Flags */}
                        <div className="mb-1.5 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {isCanceled && <span className="text-slate-400 dark:text-slate-500 text-xs">â›”</span>}
                             <span className="enterprise-surface-2 enterprise-text-muted flex items-center border border-enterprise-border-soft px-1 text-[9px] font-mono font-semibold">
                              <Hash size={8} className="mr-0.5" />
                              {getDisplayId(action.id, action.linkedNodeId)}
                            </span>
                            {isExpired && (
                               <span className="risk-critical flex items-center gap-0.5 border border-enterprise-border-strong px-1.5 py-0.5 text-[9px] font-semibold">
                                <AlertCircle size={8} /> ATRASADO
                              </span>
                            )}
                          </div>
                          <PriorityFlag impacto={action.impacto} />
                        </div>

                        {/* Título */}
                         <h4 className={`mb-2 line-clamp-3 text-[11px] font-semibold leading-snug ${isCanceled ? 'line-through enterprise-text-muted' : 'enterprise-text-primary'}`} title={action.title}>
                          {action.title}
                        </h4>

                        {/* Rodapé: Resp e Data */}
                         <div className="mt-auto flex items-center justify-between border-t border-enterprise-border-soft pt-1.5">
                           <ResponsibleAvatar name={action.responsavel} />

                           {action.prazo && (
                             <div className={`flex items-center gap-1 text-[10px] font-medium ${isExpired ? 'risk-critical' : 'enterprise-text-muted'}`}>
                               <Calendar size={10} />
                               {new Date(action.prazo).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit' })}
                             </div>
                           )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ActionsKanbanBoard;
